import { ShieldCheck } from "lucide-react";
import * as React from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Casca das telas públicas (entrar, concluir cadastro, redefinir senha): à
 * esquerda o que o produto faz (some no celular), à direita só o formulário.
 */
export function LayoutAcesso({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string;
  subtitulo: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_minmax(26rem,0.9fr)]">
      <section className="relative hidden flex-col justify-between overflow-hidden bg-fs-escuro p-12 text-fs-areia lg:flex">
        {/* Foto de fundo em baixa resolução: os overlays abaixo (degradês
            escuros + leve desfoque + grade) disfarçam a falta de nitidez. */}
        <img
          src="/login-bg.jpg"
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 size-full scale-105 object-cover blur-[3px] saturate-[0.85]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-fs-escuro/75 mix-blend-multiply"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(48rem_32rem_at_22%_18%,rgba(141,200,145,0.28),transparent_65%),radial-gradient(38rem_28rem_at_88%_88%,rgba(27,107,67,0.6),transparent_60%)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-fs-escuro/90 via-fs-escuro/45 to-fs-escuro/20"
        />

        <div className="relative flex items-center gap-3">
          <span
            aria-hidden
            className="grid size-11 place-items-center rounded-xl bg-fs-verde text-lg font-extrabold tracking-tight text-white ring-1 ring-inset ring-white/25"
          >
            Olé
          </span>
        </div>

        <div className="relative max-w-xl">
          <h2 className="text-[2.6rem] font-extrabold leading-[1.08] tracking-tight">
            Acelerador de{" "}
            <span className="text-fs-menta">Novas Operações de Etanol</span>
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-fs-claro">
            Decks fiscais por UF, roteiro contábil para o SAP e envio ao TAX.
          </p>

          <dl className="mt-10 grid max-w-lg grid-cols-2 gap-4 border-t border-white/15 pt-6">
            {[
              ["27", "UFs cobertas"],
              ["3", "decks fiscais"],
            ].map(([valor, rotulo]) => (
              <div key={rotulo}>
                <dt className="numerico text-2xl font-extrabold text-fs-menta">{valor}</dt>
                <dd className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-fs-claro/80">
                  {rotulo}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <p className="relative flex items-center gap-2 text-[11px] text-fs-claro/70">
          <ShieldCheck className="size-3.5" />
          Acesso restrito · sessão com expiração automática
        </p>
      </section>

      <section className="flex items-center justify-center bg-background p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <span
              aria-hidden
              className="grid size-11 place-items-center rounded-xl bg-fs-verde text-lg font-extrabold tracking-tight text-white"
            >
              Olé
            </span>
          </div>

          <h1 className="text-2xl font-extrabold tracking-tight text-fs-escuro">{titulo}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitulo}</p>

          {children}

          <p className="mt-8 border-t border-border pt-5 text-[11px] leading-relaxed text-muted-foreground">
            Alíquotas e tratamentos vêm do RICMS de cada UF e do ambiente regulatório federal —
            confirme com a área TAX antes do uso em produção.
          </p>
        </div>
      </section>
    </div>
  );
}

/** Faixa de erro dos formulários de acesso. */
export function AlertaErro({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="animate-surgir rounded-lg bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive ring-1 ring-inset ring-destructive/20"
    >
      {children}
    </p>
  );
}

/** Faixa de confirmação dos formulários de acesso. */
export function AlertaOk({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="status"
      className="animate-surgir rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-600/20"
    >
      {children}
    </p>
  );
}

/** Campo rotulado — o par Label + Input que todos os formulários de conta repetem. */
export function Campo({
  id,
  rotulo,
  ...props
}: { id: string; rotulo: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <Label htmlFor={id}>{rotulo}</Label>
      <Input id={id} {...props} />
    </div>
  );
}

export const SENHA_MINIMA = 8;

/** Mensagem de validação da nova senha, ou vazio se ela serve. */
export function problemaSenha(senha: string, confirmacao: string) {
  if (senha.length < SENHA_MINIMA) return `A senha precisa de ao menos ${SENHA_MINIMA} caracteres.`;
  if (senha !== confirmacao) return "A confirmação não confere com a senha.";
  return "";
}

export function mensagemDeErro(falha: unknown, padrao: string) {
  return falha instanceof Error && falha.message ? falha.message : padrao;
}
